import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {API} from "../../../types/API";
import {AxiosError, AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {WithdrawProvider} from "../../../types/WithdrawProvider";
import OutlineInput from "../../../components/OutlineInput";
import {Button} from "@material-tailwind/react";
import Loader from "../../../assets/icons/Loader";
import {InputType} from "../../../types/InputType";
import {setErrors, validateData} from "../../../reducers/validationReducer";
import {useDispatch} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {emailRegex, nameRegEx} from "./WithdrawsForm";
import {Input} from "../../../types/Input";
import {useTranslation} from "react-i18next";
import DropdownInput from "../../../components/DropdownInput";
import {WithdrawCountryResponse} from "../../../types/Responses/WithdrawsResponse";
import CountryAvatar from "../../../components/CountryAvatar";
import {CountryShort} from "../../../types/Responses/CountriesResponse";
import {APILink} from "../../../types/APILink";
import {useSystemContext} from "../../../types/contexts/SystemContext";

interface ZotaPayFormProps {
    broker: number,
    clientTransactionID?: number
}

const ZotaPayForm = ({broker, clientTransactionID}: ZotaPayFormProps) => {

    const {t} = useTranslation();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const system = useSystemContext();
    const [amount, setAmount] = useState<number>();
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [currency, setCurrency] = useState<string>();
    const [accountCurrency, setAccountCurrency] = useState<number>();
    const [country, setCountry] = useState<number>();
    const [bankProvince, setBankProvince] = useState<string>();
    const [bankCity, setBankCity] = useState<string>();
    const [accountNumber, setAccountNumber] = useState<string>();
    const [accountName, setAccountName] = useState<string>();

    const [validated, setValidated] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [countriesList, setCountriesList] = useState<WithdrawCountryResponse>();

    const dispatch = useDispatch();

    const clearData = () => {
        setInProgress(false);
        setAmount(0);
        setFirstName('');
        setLastName('');
        setEmail('');
        setCurrency('');
        setBankProvince('');
        setBankCity('');
        setAccountNumber('');
        setAccountName('');
        setValidated(false)
    }

    const getCountries = useCallback(() => {
        API.getWithdrawCountries(broker, WithdrawProvider.ZOTAPAY).then((response: AxiosResponse) => {
            setCountriesList(response.data as WithdrawCountryResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_COUNTRIES}`, toast, handleError));
    }, [toast, handleError]);

    const getClientTransaction = useCallback(() => {
        API.getWithdrawTransaction(clientTransactionID ?? 0).then((response: AxiosResponse) => {
            setFirstName(response.data.first_name);
            setLastName(response.data.last_name);
            setEmail(response.data.email);
            setCurrency(response.data.currency.code);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_TRANSACTION.replace('$transaction', (clientTransactionID ?? 0).toString())}`, toast, handleError));
    }, [toast, handleError]);

    const changeCountry = useCallback((u: string | number) => {
        setCountry(typeof u === "string" ? parseInt(u) : u);
        setValidated(false);
    }, [setCountry]);

    const changeAmount = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAmount(parseFloat(e.currentTarget.value))
        }
    }, [setValidated, setAmount]);

    const changeFirstName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFirstName(e.currentTarget.value)
        }
    }, [setValidated, setFirstName]);

    const changeBankProvince = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setBankProvince(e.currentTarget.value)
        }
    }, [setValidated, setBankProvince]);

    const changeBankCity = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setBankCity(e.currentTarget.value)
        }
    }, [setValidated, setBankCity]);

    const changeLastName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setLastName(e.currentTarget.value)
        }
    }, [setValidated, setLastName]);


    const changeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEmail(e.currentTarget.value)
        }
    }, [setValidated, setEmail]);

    const changeAccountNumber = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAccountNumber(e.currentTarget.value)
        }
    }, [setValidated, setAccountNumber]);

    const changeAccountName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAccountName(e.currentTarget.value)
        }
    }, [setValidated, setAccountName]);

    const changeCurrency = useCallback((s: string | number) => {
        if (!system || !system.system || !system.system.currencies)
            return;
        setValidated(false);
        const c = typeof s === "number" ? s : parseInt(s);
        setAccountCurrency(c);
        setCurrency(system.system.currencies.find(country => country.id === c)?.code);

    }, [setValidated, setAccountCurrency, setCurrency]);


    const handleSuccessResponse = useCallback((response: AxiosResponse) => {
        if (response.data.data) {
            clearData();
            const status = response.data.data.status;
            let message = '';
            if (status && status === 'Waiting for approve')
                message = t('admin.withdraws.approve_is_required')
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t('admin.withdraws.created_message')}<span
                className="uppercase inline-block ml-1">{message}</span></>, "bg-teal-500");
        }
    }, [toast, clearData]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        if (b) {
            const request = {
                country,
                amount,
                email,
                broker,
                account: accountCurrency,
                provider: WithdrawProvider.AWEPAY,
                first_name: firstName,
                last_name: lastName,
                bank_province: bankProvince,
                bank_city: bankCity,
                account_number: accountNumber,
                account_name: accountName,
            };
            API.makeAwePayWithdraw(request)
                .then((response: AxiosResponse) => handleSuccessResponse(response))
                .catch((e) => {
                    setInProgress(false);
                    if (e instanceof AxiosError && e.response) {
                        setErrors(e.response.data.errors);
                    }
                    ErrorHandler(e, `post${APILink.MAKE_AWEPAY_WITHDRAW}`, toast, handleError)
                })
        } else setInProgress(false);
    }, [firstName, country, accountCurrency, lastName, validated, amount, email, accountName, accountNumber, bankCity, bankProvince, broker, clientTransactionID]);

    const submit = useCallback(() => {
        setInProgress(true);
        const inputs: Input[] = [
            {key: 'country', label: t('admin.withdraws.country'), type: InputType.number, regex: '^\\d{1,3}$'},
            {key: 'account', label: t('admin.withdraws.currency'), type: InputType.number},
            {key: 'amount', label: t('admin.withdraws.amount'), type: InputType.number, regex: '^\\d{1,18}$'},
            {key: 'first_name', label: t('admin.withdraws.first_name'), type: InputType.number, regex: nameRegEx},
            {key: 'last_name', label: t('admin.withdraws.last_name'), type: InputType.number, regex: nameRegEx},
            {key: 'email', label: t('admin.withdraws.email'), type: InputType.email, regex: emailRegex},
            {key: 'bank_city', label: t('admin.withdraws.bank_city'), type: InputType.input},
            {key: 'bank_province', label: t('admin.withdraws.bank_province'), type: InputType.input},
            {key: 'account_number', label: t('admin.withdraws.account_number'), type: InputType.input},
            {key: 'account_name', label: t('admin.withdraws.account_name'), type: InputType.input},
        ];

        const data = {
            country,
            amount,
            first_name: firstName,
            last_name: lastName,
            email,
            account: accountCurrency,
            bank_city: bankCity,
            bank_province: bankProvince,
            account_number: accountNumber,
            account_name: accountName,
        };

        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));

    }, [amount, country, accountCurrency, currency, firstName, lastName, email, callback, bankCity, bankProvince, accountName, accountNumber]);


    const amountUI = useMemo(() => {
        if (!currency)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.amount_in', {currency})} type="number" validated={validated}
                          value={amount}
                          fieldName="amount"
                          onChange={changeAmount}/>
        </div>
    }, [validated, changeAmount, amount, currency]);

    const firstNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.first_name')} type="text" validated={validated} value={firstName}
                          fieldName="first_name"
                          onChange={changeFirstName}/>
        </div>
    }, [validated, firstName, changeFirstName])

    const lastNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.last_name')} type="text" validated={validated} value={lastName}
                          fieldName="last_name"
                          onChange={changeLastName}/>
        </div>
    }, [validated, lastName, changeLastName])

    const emailUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.email')} type="email" validated={validated} value={email}
                          fieldName="email"
                          onChange={changeEmail}/>
        </div>
    }, [validated, email, changeEmail])

    const countryUI = useMemo(() => {
        if (!countriesList)
            return <></>;

        return <div className="max-w-md mt-4">
            <DropdownInput
                list={countriesList.data.map((c) => {
                    return {
                        name: c.name, id: c.id, icon: <CountryAvatar country={c as CountryShort} withName={false}
                                                                     className="h-3 max-w-[1rem]"/>
                    }
                })}
                name={t('admin.withdraws.country')} selected={country} searchable={true} onChange={changeCountry}/>
        </div>
    }, [country, changeCountry, countriesList]);

    const currencyUI = useMemo(() => {
        if (!system || !system.system || !system.system.currencies || !country)
            return <></>;
        if (!countriesList || !countriesList.data)
            return <></>

        return <div className="max-w-md mt-4">
            <DropdownInput name={t('admin.withdraws.currency')} className="w-full min-w-[200px]"
                           selected={accountCurrency}
                           onChange={changeCurrency}
                           list={system.system.currencies.filter((c) => countriesList.data.find(c => c.id === country)?.currencies.includes(c.id.toString(10))).map((c) => {
                               return {
                                   name: c.code,
                                   id: c.id
                               }
                           })}/>
        </div>
    }, [validated, countriesList, accountCurrency, changeCurrency, country])


    const bankProvinceUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.bank_province')} type="text" validated={validated}
                          value={bankProvince}
                          fieldName="bank_province"
                          onChange={changeBankProvince}/>
        </div>
    }, [validated, bankProvince, changeBankProvince])


    const bankCityUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.bank_city')} type="text" validated={validated} value={bankCity}
                          fieldName="bank_city"
                          onChange={changeBankCity}/>
        </div>
    }, [validated, bankCity, changeBankCity])

    const accountNumberUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.account_number')} type="text" validated={validated}
                          value={accountNumber}
                          fieldName="account_number"
                          onChange={changeAccountNumber}/>
        </div>
    }, [validated, accountNumber, changeAccountNumber])

    const accountNameUI = useMemo(() => {
        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.account_name')} type="text" validated={validated} value={accountName}
                          fieldName="account_name"
                          onChange={changeAccountName}/>
        </div>
    }, [validated, accountName, changeAccountName])


    const submitButtonUI = useMemo(() => {
        if (!amount)
            return <></>;

        return <div className="max-w-md mt-8">
            <Button className="bg-teal-500 text-white"
                    onClick={() => submit()}
                    disabled={isInProgress}>{isInProgress ?
                <Loader/> : t('Submit')}</Button>
        </div>
    }, [amount, isInProgress, submit]);


    useEffect(() => {
        clearData();
        // getCurrencyAccount();
        getCountries();
        if (clientTransactionID) {
            getClientTransaction();
        }
    }, [broker, clientTransactionID]);

    return <>
        {countryUI}
        {currencyUI}
        {firstNameUI}
        {lastNameUI}
        {emailUI}
        {amountUI}
        {bankProvinceUI}
        {bankCityUI}
        {accountNumberUI}
        {accountNameUI}
        {submitButtonUI}
    </>
}

export default memo(ZotaPayForm);
